// English, the source of every other language (src/i18n/index.js). Keys are
// flat; {name} is a placeholder; `_one` / `_other` are plural forms.
// Game terms follow the N5 rules as Corvus Belli writes them.
/** @type {Record<string, string>} */
export default {
  // Sides
  'side.active': 'Active',
  'side.reactive': 'Reactive',

  // Abbreviations in dice lines ("B4 SV14 PS9") and on weapon buttons
  'abbr.B': 'B',
  'abbr.SD': 'SD',
  'abbr.SV': 'SV',
  'abbr.PS': 'PS',
  'abbr.SR': 'SR',
  'abbr.CC': 'CC',

  // Attributes, as the unit profile heads them
  'stat.mov': 'MOV',
  'stat.cc': 'CC',
  'stat.bs': 'BS',
  'stat.ph': 'PH',
  'stat.wip': 'WIP',
  'stat.arm': 'ARM',
  'stat.bts': 'BTS',
  'stat.w': 'W',
  'stat.str': 'STR',
  'stat.s': 'S',

  // Unit types, as the pickers name them
  'typeName.LI': 'Light Infantry',
  'typeName.MI': 'Medium Infantry',
  'typeName.HI': 'Heavy Infantry',
  'typeName.TAG': 'TAGs',
  'typeName.REM': 'REMs',
  'typeName.SK': 'Skirmishers',
  'typeName.WB': 'Warbands',
  'typeName.VH': 'Vehicles',

  // Distances (src/i18n/index.js t.distance): inches, 2.5 cm each
  'dist.cmPerUnit': '2.5',
  'dist.value': '{n}"',
  'dist.band': '{from}-{to}"',
  // The picked distance on the range bar: 32".
  'dist.picked': '{n}"',

  // Ammunition, as players write it
  'ammo.N': 'N',
  'ammo.DA': 'DA',
  'ammo.EXP': 'EXP',
  'ammo.AP': 'AP',
  'ammo.T2': 'T2',
  'ammo.SHOCK': 'Shock',
  'ammo.STUN': 'Stun',
  'ammo.PLASMA': 'Plasma',
  'tag.cont': 'Cont',
  'tag.contUpper': 'CONT',
  'tag.blast': 'Blast',
  'trait.continuousDamage': 'Continuous Damage',
  'save.armZero': 'ARM=0',

  // Choices that are not weapons
  'pseudo.dodge': 'Dodge',
  'pseudo.bangbomb': 'Bangbomb',
  'pseudo.reset': 'Reset',
  'pseudo.zeroPain': 'Zero Pain',
  'pseudo.zeroPainMod': 'Zero Pain ({mod})',
  'pseudo.none': 'No ARO',

  // Skills and equipment, as the ledger and the notes name them
  'skill.berserk': 'Berserk',
  'skill.berserkMod': 'Berserk ({mod})',
  'skill.martialArts': 'Martial Arts L{level}',
  'skill.ccAttackMod': 'CC Attack ({mod})',
  'skill.bsAttackMod': 'BS Attack ({mod})',
  'skill.surpriseAttackMod': 'Surprise Attack ({mod})',
  'skill.dodgeMod': 'Dodge ({mod})',
  'skill.mimetismMod': 'Mimetism ({mod})',
  'skill.sixthSense': 'Sixth Sense',
  'skill.sixthSenseDodge': 'Sixth Sense (Dodge)',
  'skill.naturalBornWarrior': 'Natural Born Warrior',
  'skill.warhorse': 'Warhorse',
  'skill.combatInstinct': 'Combat Instinct',
  'skill.neurocinetics': 'Neurocinetics',
  'skill.marksmanship': 'Marksmanship',
  'skill.limitedCover': 'Limited Cover',
  'skill.foxhole': 'Foxhole',
  'supportware.assistedFire': 'Assisted Fire',
  'supportware.enhancedReaction': 'Enhanced Reaction',
  'supportware.enhancedReactionBurst': 'Enhanced Reaction: B{burst} in ARO',
  'skill.immunity': 'Immunity ({what})',
  'equip.albedo': 'Albedo',
  'equip.bangbombMod': 'Bangbomb ({mod})',
  'equip.firewall': 'Firewall',
  'equip.firewallMod': 'Firewall ({mod})',
  'equip.ecmHacker': 'ECM: Hacker ({mod})',
  'equip.nanoscreen': 'Nanoscreen',
  'equip.msv': 'MSV{level}',
  'weapon.withMod': '{name} ({mod})',
  'cc.improvised': 'Improvised',
  'cc.loadoutOrCcAttack': 'loadout / CC Attack',
  'cc.alliesInContact_one': '{count} ally in contact',
  'cc.alliesInContact_other': '{count} allies in contact',

  // Smoke and White Noise
  'zone.smoke': 'Smoke',
  'zone.whiteNoise': 'White Noise',
  'zone.zeroVisibility': '{zone}, Zero Visibility',
  'zone.poorVisibility': '{zone}, Poor Visibility',
  'zone.noMsv': 'no MSV',

  // Wound states
  'state.wounds_one': '{count} wound',
  'state.wounds_other': '{count} wounds',
  'state.unc': 'Unconscious',
  'state.nwi': 'NWI',
  'state.dogged': 'Dogged',
  'state.twice': '{state} ×2',
  'state.dead': 'Dead',
  'state.isolated': 'Isolated',
  'state.stunned': 'Stunned',
  'state.immobilized-b': 'Immobilized-B',
  'state.targeted': 'Targeted',
  'state.possessed': 'Possessed',

  // "How the dice were built" (rules/ledger.js)
  'ledger.teamOpsStat': '{stat} (Team-Ops: {upgrade})',
  'ledger.range': 'range {from}–{to}"',
  'ledger.fireteam': 'Fireteam {size}',
  'ledger.outOfRange': 'out of range',
  'ledger.outOfRangeNote': 'out of range: always fails',
  'ledger.loadout': 'loadout',
  'ledger.loadoutOrBsAttack': 'loadout / BS Attack',
  'ledger.noLof': 'No LoF',
  'ledger.cancelled': 'cancelled',
  'ledger.albedoOnly': 'only vs MSV or Marksmanship',
  'ledger.cover': 'cover',
  'ledger.foxholeCover': 'Foxhole cover',
  'ledger.commsAttack': 'Comms Attack',
  'ledger.closeCombat': 'close combat',
  'ledger.template': 'template',
  'ledger.templateWeapon': 'Template',
  'ledger.modCap': 'MODs cap at ±{cap}',
  'ledger.belowZero': 'below 0: always fails',
  'ledger.cappedAt': 'capped at {max}',
  'ledger.dodgePh': 'Dodge (PH={ph})',
  'ledger.dodgeSd': 'Dodge (+SD)',
  'ledger.dodgeArm': 'Dodge (ARM +{extra})',
  'ledger.aroOneDie': 'ARO: one die',
  'ledger.burstCap': 'B caps at {max}',
  'ledger.armZeroBy': '{weapon}: ARM = 0',
  'ledger.armZero': 'ARM = 0',
  'ledger.notBelowZero': 'not below 0',
  'ledger.halved': '{stat} halved',
  'ledger.halvedAp': '{stat} halved (AP)',
  'ledger.noEffectOnTarget': 'no effect on this target',
  'ledger.savesPerHit_one': '{ammo}: {count} save per hit',
  'ledger.savesPerHit_other': '{ammo}: {count} saves per hit',
  'ledger.woundsPerFailure_one': '{ammo}: {count} wound per failed save',
  'ledger.woundsPerFailure_other': '{ammo}: {count} wounds per failed save',
  'ledger.alsoBts': '{ammo}: also a BTS save',
  'ledger.viral': 'Viral (DA+Shock)',
  'ledger.noEffectImmune': 'no effect: target immune',
  'ledger.noEffect': 'no effect',
  'ledger.noLofZone': 'No LoF ({zone})',
  'ledger.notRolled': '{choice}: not rolled',
  'ledger.cantTarget': '{program} can\'t target this trooper.',
  'ledger.programMod': '{program} ({mod})',
  'ledger.resetOnlyComms': 'Reset only evades Comms Attacks.',
  'ledger.dodgeNotComms': 'Dodge can\'t evade a Comms Attack; Reset can.',

  // Notes, warnings and errors next to a matchup (rules/matchup.js)
  'note.side': '{side}: {text}',
  'note.text': '{text}',
  'note.ignoresMod': '{by}; the opponent\'s {mod} has no effect',
  'note.modCap': 'MODs add up to {sum}, capped at {cap}',
  'note.neurocinetics': 'Neurocinetics; Burst reduced to 1 in the Active Turn',
  'note.shock': 'Shock against VITA 1; a failed save is Dead (no Unconscious, NWI or Dogged)',
  'note.stateCaused': '{weapon}; a failed save leaves the target {state}, not wounded',
  'note.cantTarget': '{program} can\'t target this trooper; it isn\'t rolled',
  'note.contImmune': 'target has Immunity (Continuous Damage); Continuous Damage ignored',
  'note.vulnerability': 'target has Vulnerability ({vulnerable}); Immunity ({immunity}) does not apply',
  'note.resetOnlyComms': 'Reset only evades Comms Attacks; the {weapon} is rolled unopposed',
  'note.dodgeNotComms': 'Dodge can\'t evade a Comms Attack ({weapon}), only Reset can; it is rolled unopposed',
  'note.noLof': 'no line of fire through {zone}; its attack can\'t be declared',
  'note.directTemplate': 'Direct Template: no Face to Face Roll; each attack is rolled on its own',
  'note.targetImmunity': 'target has Immunity ({what}); {effect}',
  'note.treatedAsN': '{ammo} treated as N',
  'note.ignored': '{what} ignored',
  'warning.noEffect': '{weapon} has no effect on a target with {by}; its hits cause no damage',
  'warning.nonLethal': '{weapon} is non-lethal; results shown as wounds',
  'warning.upgradeWeapon': '{weapon} (upgrade) not supported by the calculator',
  'warning.specOps': 'Spec-Ops upgrades and SpecBall not supported yet',
  'error.noBsAttack': 'this profile cannot make BS attacks; pick Dodge',
  'error.noCcAttack': 'this profile cannot make CC attacks; pick Dodge',
  'error.noBsAttackAro': 'this profile cannot make BS attacks; pick Dodge or No ARO',
  'error.noCcAttackAro': 'this profile cannot make CC attacks; pick Dodge or No ARO',

  // App chrome
  'mode.matchup': 'Matchup',
  'mode.classic': 'Classic',
  'menu.menu': 'Menu',
  'menu.appMenu': 'App menu',
  'menu.calculator': 'Calculator',
  'menu.classicLong': 'Classic — type the numbers',
  'menu.opensNextTime': 'opens next time',
  'menu.saved': 'Saved',
  'menu.savedRolls': 'Saved rolls',
  'keys.back': 'Back',
  'keys.closeCap': 'Close',
  'keys.close': 'close',
  'keys.move': 'move',
  'keys.delete': 'delete',
  'keys.pick': 'pick',
  'keys.intoLoadouts': 'into loadouts',
  'load.failed': 'Could not load {what}.',
  'load.retry': 'Try again',
  'load.armyData': 'the army data',
  'load.trooperList': 'the trooper list',
  'share.copied': 'Link copied',
  'share.failed': 'Could not copy the link',

  // Settings
  'settings.title': 'Settings',
  'settings.resultsCard': 'Results card',
  'settings.headlineNotePhone': 'The big number on the results card; swipe the card sideways to change it there. The others are one swipe up.',
  'settings.headlineNoteDesk': 'The big number on the results card. Click the dots under it to change it there too.',
  'settings.startingFactions': 'Starting factions',
  'settings.activeSide': 'Active side',
  'settings.reactiveSide': 'Reactive side',
  'settings.lastUsed': 'Last used',
  'settings.language': 'Language',
  'settings.appLanguage': 'App language',
  'settings.automatic': 'Automatic ({language})',
  'settings.yourData': 'Your data',
  'settings.clearRecents': 'Clear recent troopers',
  'settings.clearRecentsAsk': 'Clear recent troopers and factions?',
  'settings.recentsCleared': 'Recents cleared',
  'settings.deleteSaved': 'Delete all saved rolls',
  'settings.deleteSavedAsk': 'Delete every saved roll? This cannot be undone.',
  'settings.savedDeleted': 'Saved rolls deleted',
  'settings.savedOnDevice': 'Saved rolls live only on this device.',

  // About
  'about.title': 'About',
  'about.intro': 'Face-to-face odds for Infinity N5. Odds are exact: every possible roll is counted, not simulated.',
  'about.madeBy': 'Made with ❤️ for the Infinity community by Khepri and Bebop.',
  'about.source': '<a>Source on GitHub</a>',
  'about.poweredBy': 'Powered by <a>icepool</a> by <b>HighDiceRoller</b>',
  'about.classic': 'Hate this new version? Go back to the <a>Classic N5 calculator</a>!',
  'about.n4': 'N4 calculator',
  'about.contact': 'Bugs? Suggestions? Let me know at <a>{email}</a>.',
  'about.trademark': 'Infinity the Calculator is a fan made project with no affiliation to Corvus Belli. Thank you CB for being super supportive of fan projects like this one!',
  'about.armyData': 'Army data',
  'about.armyDataSnapshot': 'Army data: Corvus Belli\'s Infinity Army{date} (unofficial snapshot)',
  'about.rules': 'Rules',
  'about.app': 'App',

  // Headline numbers (results/headline.js)
  'headline.wpo': 'Wounds per order',
  'headline.wpoHint': 'Average wounds this order causes',
  'headline.win': 'Wins face to face',
  'headline.winHint': 'Chance to win the roll',
  'headline.wound': 'At least one wound',
  'headline.woundHint': 'Chance the target takes a wound',
  'headline.wpoLabel': 'wounds / order',
  'headline.winsLabel': 'wins face to face',
  'headline.woundLabel': 'at least one wound',
  'headline.resets': 'resets',
  'headline.dodges': 'dodges',
  'headline.notFaceToFace': 'Not face to face',
  'headline.bigNumber': 'Big number',

  // Side cards
  'side.trooper': '{side} trooper',
  'side.choose': 'Choose a trooper',
  'side.recent': 'Recent troopers',
  'side.pressKey': 'or press <kbd>{key}</kbd> to search',
  'side.weapon': 'Weapon',
  'side.notRolled': 'Not rolled: {weapons}',
  'chip.cover': 'Cover',
  'chip.firewall': 'Firewall',
  'chip.targeted': 'Targeted',
  'chip.hacked': 'Hacked',
  'chip.immA': 'IMM-A',
  'chip.sapper': 'Sapper',
  'chip.fireteam': 'Fireteam',
  'chip.allies': 'Allies',
  'chip.supportware': 'Supportware',
  'chip.none': 'No {label}',
  'chip.surpriseAttackMod': 'Surprise attack ({mod})',
  'matchup.clearBoth': 'Clear both troopers',
  'matchup.modifyInClassic': 'Modify in classic',
  'matchup.modsWait': 'Every modifier, with where it came from, once both sides are set.',

  // Range selector
  'range.title': 'Range',
  'range.close': 'Close range',
  'range.open': 'open range',
  'range.unused': 'Close combat: range has no effect',
  'range.band': '{band}: active {active}, reactive {reactive}',
  'range.perSide': 'Range per side',
  'range.swap': 'Swap active and reactive',
  'range.split': 'Split range',
  'range.noLof': 'no LoF',
  'range.between': 'Between the troopers',
  'range.zoneBetween': '{zone} between them',
  'range.bothBetween': 'Smoke and White Noise between them',
  'range.zones': 'Smoke and White Noise',

  // Results
  'results.title': 'Results',
  'results.save': 'Save this roll',
  'results.unsave': 'Saved; remove from saved rolls',
  'results.share': 'Share a link to this roll',
  'results.engineLoading': 'Icepool engine getting ready to roll…',
  'results.chooseBoth': 'Choose both troopers to see the odds',
  'results.chooseActive': 'Choose the active trooper',
  'results.chooseReactive': 'Choose the reactive trooper',
  'results.ready': 'Ready',
  'results.f2fBar': 'Face to face: active wins {active}, nobody {none}, reactive wins {reactive}',
  'results.sideWins': '{side} wins',
  'results.sideWinsRoll': '{side} wins the roll',
  'results.nobodyWins': 'Nobody wins',
  'results.nobodyWounded': 'Nobody wounded',
  'results.atLeast': '{count}+ wounds',
  'results.allSaved': 'all saved',
  'results.bothFail': 'both fail',
  'results.bothMissOrTie': 'Both miss or tie',
  'results.savesAll': 'Saves all',
  'results.overlapNote': '1+ / 2+ / 3+ = at least that many wounds, so they overlap',
  'results.openHint': 'Show full results. Left and right arrows change the big number.',
  'results.stateTip': '{state} · exactly {exactly} · at least {atLeast}',
  'results.woundsByState': 'Wounds by state',
  'results.exactly': 'Exactly',
  'results.atLeastHead': 'At least',
  'results.overOrders': '{now} this order · {over} over {orders} orders',
  'results.faceToFace': 'Face to face',
  'engine.loading': 'Loading the dice engine',
  'engine.startFailed': 'Could not start the dice engine: {error}',
  'engine.done': 'Done! Took {ms}ms to calculate all {rolls} possible rolls.',
  'engine.failed': 'Calculation failed: {error}',
  'engine.workerError': 'Worker error: {error}',
  'f2f.title': 'Face to face breakdown',
  'f2f.lead': 'Chance of obtaining the best die. The higher best die wins the roll, equal cancel. The bright part of the bar is when that best die wins.',
  'f2f.summary': 'Active wins {active}, nobody {none}, reactive {reactive}',
  'f2f.oneDie': '1 die',
  'f2f.bestOf': 'best of {count} dice',
  'f2f.bothMiss': 'both miss {p}',
  'f2f.bothCrit': 'both Critical {p}',
  'f2f.same': 'same number {p}',
  'f2f.withCrit': 'with a Critical {p}',
  'f2f.backHint': 'tap the wound bar for wounds',
  'unopposed.saved': 'saved',
  'unopposed.miss': 'miss',
  'unopposed.atLeast_one': '{count}+ wound',
  'unopposed.atLeast_other': '{count}+ wounds',
  'unopposed.hitsAllSaved': 'hits, all saved',
  'unopposed.misses': 'misses',
  'unopposed.attack': '{name} attack',
  'unopposed.both': 'both are wounded',
  'unopposed.only': 'only {name} wounded',
  'unopposed.nobody': 'nobody wounded',
  'unopposed.explain': 'Not a face to face roll. A Direct Template doesn\'t roll, so nothing is opposed: each attack is resolved on its own and <b>both can wound</b> this order.',
  'unopposed.thisOrder': 'What happens this order',

  // The ledger cards
  'ledger.mods': '{side} mods',
  'ledger.noAro': 'No ARO: does not roll.',
  'ledger.successValue': 'Success Value',
  'ledger.saveTitle': 'Save the target makes',
  'ledger.burstTitle': 'Burst',
  'ledger.ammoTitle': 'Ammo',
  'ledger.templateHits': 'Direct Template: hits automatically',
  'ledger.profile': 'profile',
  'ledger.weapon': 'weapon',
  'ledger.rule': 'rule',

  // Classic calculator
  'classic.side': '{side} side',
  'classic.burst': 'Burst',
  'classic.specialDice': 'Special dice',
  'classic.burstZero': 'Burst 0: no roll, the other side is unopposed',
  'classic.successValue': 'Success Value',
  'classic.opponentPs': 'Opponent PS',
  'classic.opponentPsArm': 'Opponent PS ARM',
  'classic.opponentPsBts': 'Opponent PS BTS',
  'classic.minus': '{name} minus {n}',
  'classic.plus': '{name} plus {n}',
  'classic.ammunition': 'Ammunition',
  'classic.plasma': 'PLASMA',
  'classic.critImmune': 'Immunity (Critical)',
  'classic.directTemplate': 'Direct Template',
  'classic.fixedDie': 'Fixed value die (AC2)',
  'classic.sideWpo': '{side} wounds / order',
  'classic.orMore_one': '{count} or more wounds',
  'classic.orMore_other': '{count} or more wounds',
  'classic.nobody': '{p} nobody',
  'classic.wounds': 'Wounds',
  'classic.neither': 'Neither side causes a wound',
  'classic.winsAllSaved': '{side} wins, every hit saved',

  // Saved rolls
  'roll.vs': 'vs',
  'roll.cover': 'Cover',
  'roll.firewall': 'Firewall {mod}',
  'roll.closeCombat': 'Close combat',
  'roll.surpriseAttack': 'Surprise attack',
  'roll.allies_one': '{count} ally',
  'roll.allies_other': '{count} allies',
  'saved.saved': 'Saved',
  'saved.removed': 'Removed from saved rolls',
  'saved.deleted': 'Roll deleted',
  'saved.delete': 'Delete',
  'saved.deleteThis': 'Delete this roll',
  'saved.load': 'load',
  'saved.rollLabel': '{a} against {r}{setup}. Load this roll',
  'saved.whichCalculator': 'Which calculator',
  'saved.empty': 'Nothing saved yet. Open the results and tap Save to keep a roll here.',
  'saved.hintPhone': 'Tap to load the whole setup · swipe left to delete',
  'saved.hintDesk': 'Click a roll to load the whole setup',

  // Trooper pickers
  'picker.search': 'Search troopers',
  'picker.placeholder': 'Unit or weapon, e.g. fus ml',
  'picker.placeholderDesk': 'Search troopers and weapons…',
  'picker.cancel': 'Cancel',
  'picker.loading': 'Loading units…',
  'picker.faction': 'Faction',
  'picker.side': 'Side',
  'picker.sideKey': 'side',
  'picker.all': 'All',
  'picker.allFactions': 'All factions',
  'picker.recent': 'Recent',
  'picker.recentIn': 'Recent in {faction}',
  'picker.otherFactions': 'In other factions',
  'picker.pickFaction': 'Pick a faction',
  'picker.shortCombined': 'Combined',
  'picker.shortNa': 'NA Armies',
  'picker.units_one': '{count} unit',
  'picker.units_other': '{count} units',
  'picker.profiles_one': '{count} profile',
  'picker.profiles_other': '{count} profiles',
  'picker.profilesHead': 'Profiles',
  'picker.unitsHead': 'Units',
  'picker.allUnits': 'All units A–Z',
  'picker.browseByType': 'Browse {faction} by type',
  'picker.browseByFaction': 'Browse by faction',
  'picker.sectorialsNote': 'Sectorials fold into their vanilla army; mercenary companies into Non-Aligned Armies.',
  'picker.sectorialsIncluded': 'all sectorials included',
  'picker.unitType': 'Unit type',
  'picker.noMatchesIn': 'No {faction} matches for “{query}”',
  'picker.nothingMatches': 'Nothing matches “{query}”',
  'picker.countIn': '{count} in {faction}',
  'picker.show': 'Show',
  'picker.searchAll': 'Search all factions',
  'picker.closeMatch': 'close match',
  'picker.soundsLike': 'sounds like',
  'picker.pts': 'pts',
  'picker.swc': 'SWC',
  'picker.troopers': 'Troopers',
  'picker.profile': 'Profile',
  'picker.loadouts': 'Loadouts',
  'picker.loadoutCount_one': '{count} loadout',
  'picker.loadoutCount_other': '{count} loadouts',
  'picker.use': 'Use',
  'picker.useAndChange': 'Use and change {side}',
  'picker.useAndChoose': 'Use and choose {side}',
  'picker.showing': 'Showing {shown} of {matched} · keep typing to narrow it down',
  'picker.pickOnLeft': 'Pick a unit on the left.',
  'reach.long': 'Long range',
  'reach.mid': 'Mid range',
  'reach.close': 'Close',
  'reach.other': 'No ranged weapon',
};
